import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { formatApiErrorDetail } from "../lib/api";
import BrandWordmark from "../components/BrandWordmark";
import Slogan from "../components/Slogan";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      nav(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 p-6">
      <div className="w-full max-w-sm fade-up">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Flame className="w-6 h-6 text-blue-500" />
          <BrandWordmark size="md" className="text-white" />
        </Link>
        <Slogan rotate={3500} className="mb-3" testId="register-slogan" />
        <h2 className="font-display text-4xl uppercase leading-none text-white">Create account</h2>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label className="text-xs tracking-widest uppercase text-neutral-400">Name</Label>
            <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-2 bg-neutral-900 text-white border-neutral-800 focus:border-blue-500 rounded-none h-11" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-neutral-400">Email</Label>
            <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 bg-neutral-900 text-white border-neutral-800 focus:border-blue-500 rounded-none h-11" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase text-neutral-400">Password</Label>
            <Input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-2 bg-neutral-900 text-white border-neutral-800 focus:border-blue-500 rounded-none h-11" />
          </div>
          <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11 text-base">
            {loading ? "Creating…" : "Create Account"}
          </Button>
        </form>
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-neutral-800" />
          <span className="text-xs uppercase tracking-widest text-neutral-500">OR</span>
          <div className="flex-1 h-px bg-neutral-800" />
        </div>
        <Button data-testid="google-register-btn" type="button" onClick={googleLogin} variant="outline" className="w-full bg-transparent text-white border-neutral-700 hover:bg-neutral-900 rounded-none uppercase tracking-wider font-display h-11">
          Continue with Google
        </Button>
        <p className="text-sm text-neutral-400 mt-6 text-center">
          Already in? <Link to="/login" className="text-blue-500 font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
